import FormHelpers from '../../../components/form-helpers';
import LanguageUtility from '../../../utilities/language-utility';
import OfferUtility from '../../../utilities/offer-utility';
import {Tooltip} from 'bootstrap';
import ConfirmationModal from '../../../components/confirmation-modal';
import PluginAction from '../../plugin-action';

/**
 * Type describing the available properties.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface FormProperties {
    statusRadios: NodeListOf<HTMLInputElement>,
    priceField: HTMLInputElement,
    priceVariantSelect: HTMLSelectElement,
    priceTypeSelect: HTMLSelectElement,
    sideOfferRadios: NodeListOf<HTMLInputElement>,
    defaultSideOfferRadio: HTMLInputElement,
    vendorField: HTMLInputElement,
    packagePriceContainer: HTMLElement | null,
    calculatedPriceElement: HTMLElement | null,
    pricePerPersonTooltip: HTMLElement | null,
    offerTitleField: HTMLInputElement,
    offerTableField: HTMLInputElement,
    offerIdField: HTMLInputElement
}

/**
 * Type describing the available values of the form.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface FormValues {
    status: number,
    price: number,
    priceVariant: number,
    priceType: number,
    sideOffer: number,
    vendor: string,
    offerTitle: string,
    offerTable: string,
    offerId: number
}

/**
 * Type describing the available status.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface Status {
    notDecided: number,
    notNeeded: number,
    sideOffer: number,
    offer: number
}

/**
 * Type describing the available price variants.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface PriceVariant {
    perPerson: number,
    package: number
}

/**
 * Action for offer checklist step form.
 *
 * @author Reinhard Führicht <rf@typoheads.at>
 */
export default class OfferFormAction extends PluginAction {
    /**
     * Available status. Also set in PHP class OfferChecklistStep.
     */
    public static readonly status: Status = {
        notDecided: 0,
        notNeeded: 1,
        sideOffer: 2,
        offer: 3
    };

    /**
     * Available price variants. Also set in PHP class OfferChecklistStep.
     */
    public static readonly priceVariant: PriceVariant = {
        perPerson: 0,
        package: 1
    };

    /**
     * Form containing the chosen offer configuration.
     */
    private form: HTMLFormElement;

    /**
     * Contains all available form elements.
     */
    private availablePropertiesContainer: HTMLElement;

    /**
     * Contains additional fields for the side offer status.
     */
    private readonly sideOffersContainer: HTMLElement | null;

    /**
     * Contains additional fields for the offer status.
     */
    private readonly offerContainer: HTMLElement;

    /**
     * Containers for properties of a selected status, price variant and/or side offer.
     */
    private propertiesContainers: NodeListOf<HTMLElement>;

    /**
     * Contains all form properties.
     */
    private properties: FormProperties;

    /**
     * Contains list of offers on the current page.
     */
    private readonly offerListContainers: NodeListOf<HTMLElement>;

    /**
     * Whether the current checklist step has been saved before or not.
     */
    private readonly isSaved: boolean = false;

    /**
     * Number of pupils as defined in the "settings" checklist step.
     */
    private readonly numberOfPupils: number = 0;

    /**
     * Number of attendants as defined in the "settings" checklist step.
     */
    private readonly numberOfAttendants: number = 0;

    constructor(container: HTMLElement) {
        super(container);
        const form = this.container.querySelector<HTMLFormElement>('#checklist-step-form');
        const availablePropertiesContainer = this.container.querySelector<HTMLElement>('.available-properties');
        const offerContainer = this.container.querySelector<HTMLElement>('.status-3');
        const propertiesContainers = this.container.querySelectorAll<HTMLElement>('.properties');
        const offerListContainers = document.querySelectorAll<HTMLElement>('.offer-list');
        if (!form || !availablePropertiesContainer || !offerContainer || !propertiesContainers || !offerListContainers) {
            throw new Error('Required elements missing.');
        }

        this.form = form;
        this.availablePropertiesContainer = availablePropertiesContainer;
        this.sideOffersContainer = this.container.querySelector<HTMLElement>('.status-2');
        this.offerContainer = offerContainer;
        this.propertiesContainers = propertiesContainers;
        this.offerListContainers = offerListContainers;

        // Get all available properties
        const statusRadios = this.container.querySelectorAll<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][status]"]');
        const priceField = this.container.querySelector<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][price]"]');
        const priceVariantSelect = this.container.querySelector<HTMLSelectElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][priceVariant]"]');
        const priceTypeSelect = this.container.querySelector<HTMLSelectElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][priceType]"]');
        const sideOfferRadios = this.container.querySelectorAll<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][sideOffer]"]');
        const defaultSideOfferRadio = this.container.querySelector<HTMLInputElement>('.default-side-offer');
        const vendorField = this.container.querySelector<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][vendor]"]');
        const packagePriceContainer = this.container.querySelector<HTMLElement>('.package-price');
        let calculatedPriceElement = null;
        let pricePerPersonTooltip = null;
        if (packagePriceContainer) {
            calculatedPriceElement = packagePriceContainer.querySelector<HTMLElement>('.calculated-price');
            pricePerPersonTooltip = packagePriceContainer.querySelector<HTMLElement>('.price-info-tooltip');
        }
        const offerTitleField = this.container.querySelector<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][offerTitle]"]');
        const offerTableField = this.container.querySelector<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][offerTable]"]');
        const offerIdField = this.container.querySelector<HTMLInputElement>('[name="tx_sitepackage_offercheckliststep[checklistStep][offerId]"]');

        // Check for mandatory elements; side offers or price info fields may not be present
        if (!statusRadios || statusRadios.length === 0 || !priceField || !priceVariantSelect || !priceTypeSelect || !vendorField || !offerTitleField || !offerTableField || !offerIdField || !sideOfferRadios || !defaultSideOfferRadio) {
            throw new Error('Required elements missing.');
        }

        this.properties = {
            statusRadios: statusRadios, // This may be a hidden field to reset side offers on submitting, if no side offers are available
            priceField: priceField,
            priceVariantSelect: priceVariantSelect,
            priceTypeSelect: priceTypeSelect,
            sideOfferRadios: sideOfferRadios,
            defaultSideOfferRadio: defaultSideOfferRadio,
            vendorField: vendorField,
            packagePriceContainer: packagePriceContainer,
            calculatedPriceElement: calculatedPriceElement,
            pricePerPersonTooltip: pricePerPersonTooltip,
            offerTitleField: offerTitleField,
            offerTableField: offerTableField,
            offerIdField: offerIdField
        };

        // The option to only show the offer status is active
        if (this.settings.onlyShowOfferStatus) {
            if (!this.getOfferRadio()) {
                throw new Error('Offer status radio missing.');
            }
        }
        // Regular offer step; needs all status radio buttons to be present
        else {
            // The side offer radio button may be missing, if no side offers are available
            if (!this.getNotDecidedRadio() || !this.getNotNeededRadio() || !this.getOfferRadio()) {
                throw new Error('Status radios missing.');
            }
        }

        if (this.form.dataset.isSaved) {
            this.isSaved = this.form.dataset.isSaved === '1';
        }
        if (this.form.dataset.numberOfPupils) {
            this.numberOfPupils = parseInt(this.form.dataset.numberOfPupils);
        }
        if (this.form.dataset.numberOfAttendants) {
            this.numberOfAttendants = parseInt(this.form.dataset.numberOfAttendants);
        }

        // If this is a fresh step, not yet saved, then select the first status by default
        if (!this.getSelectedStatusRadio()) {
            this.properties.statusRadios.item(0).checked = true;
        }

        // If the current checklist step has never been saved before, reset all fields to setup correct default values
        if (!this.isSaved) {
            this.resetProperties();
        }

        this.updateFields();
        this.initializeForm();
        this.initializeOfferList();
        this.initializeModals();
        OfferFormAction.emitter.emit('ready', this);
    }

    /**
     * Gets the currently selected status radio button.
     *
     * @returns {HTMLInputElement|null}
     */
    private getSelectedStatusRadio(): HTMLInputElement | null {
        for (const item of this.properties.statusRadios) {
            if (item.checked) {
                return item;
            }
        }
        return null;
    }

    /**
     * Gets the radio button for marking a step as "not decided".
     *
     * @returns {HTMLInputElement|null}
     */
    private getNotDecidedRadio(): HTMLInputElement | null {
        return document.querySelector<HTMLInputElement>('#status0');
    }

    /**
     * Gets the radio button for marking a step as "not needed".
     *
     * @returns {HTMLInputElement|null}
     */
    private getNotNeededRadio(): HTMLInputElement | null {
        return document.querySelector<HTMLInputElement>('#status1');
    }

    /**
     * Gets the radio button that sets a side offer for the step.
     *
     * @returns {HTMLInputElement|null}
     */
    private getSideOfferRadio(): HTMLInputElement | null {
        return document.querySelector<HTMLInputElement>('#status2');
    }

    /**
     * Gets the radio button that sets an offer for the step.
     *
     * @returns {HTMLInputElement|null}
     */
    private getOfferRadio(): HTMLInputElement | null {
        return document.querySelector<HTMLInputElement>('#status3');
    }

    /**
     * Gets the currently selected side offer radio button.
     *
     * @returns {HTMLInputElement|null}
     */
    private getSelectedSideOfferRadio(): HTMLInputElement | null {
        // No side offers available
        if (!this.sideOffersContainer) {
            return null;
        }

        for (const item of this.properties.sideOfferRadios) {
            if (item.checked) {
                return item;
            }
        }
        return null;
    }

    /**
     * Checks if the package price calculation is activated.
     *
     * @returns {boolean} True when some calculation is activated, otherwise false
     */
    private isPackagePriceCalculationActivated(): boolean {
        return this.settings.enablePackagePriceCalculation;
    }

    /**
     * Checks if some participants have been set in the "settings" checklist step.
     *
     * @returns {boolean} True when some participants are present, otherwise false
     */
    private hasParticipants(): boolean {
        return this.numberOfPupils > 0 || this.numberOfAttendants > 0;
    }

    /**
     * Resets the status radio buttons to a default, unselected value.
     */
    private resetStatusRadios(): void {
        this.properties.statusRadios.forEach(item => {
            item.checked = false;
        });
    }

    /**
     * Resets the side offer radio buttons to a default, unselected value.
     */
    private resetSideOfferRadios(): void {
        // No side offers available
        if (!this.sideOffersContainer) {
            // Select default side offer radio button to reset value on submit
            this.properties.defaultSideOfferRadio.checked = true;
            return;
        }

        this.properties.sideOfferRadios.forEach(item => {
            item.checked = false;
        });
        this.properties.defaultSideOfferRadio.checked = true;
    }

    /**
     * Resets all properties except the status radios. Useful for resetting the form default values when use cases are switched.
     */
    private resetProperties(): void {
        this.resetSideOfferRadios();
        this.resetPriceFields();
        this.resetVendorField();
        this.resetOfferTitleField();
        this.resetOfferTableField();
        this.resetOfferIdField();
    }

    /**
     * Resets the price field to a default, empty value.
     */
    private resetPriceField(): void {
        this.properties.priceField.value = '0';
    }

    /**
     * Resets the price variant select to a default, empty value.
     */
    private resetPriceVariantSelect(): void {
        this.properties.priceVariantSelect.value = '0';
    }

    /**
     * Resets the price type select to a default, empty value.
     */
    private resetPriceTypeSelect(): void {
        this.properties.priceTypeSelect.value = '0';
    }

    /**
     * Resets all fields related to the price or price calculation.
     */
    private resetPriceFields(): void {
        this.resetPriceField();
        this.resetPriceVariantSelect();
        this.resetPriceTypeSelect();

        if (this.isPackagePriceCalculationActivated()) {
            this.updatePerPersonPrice();
        }
    }

    /**
     * Resets the vendor field to a default, empty value.
     */
    private resetVendorField(): void {
        this.properties.vendorField.value = '';
    }

    /**
     * Resets the offer title field to a default, empty value.
     */
    private resetOfferTitleField(): void {
        this.properties.offerTitleField.value = '';
    }

    /**
     * Resets the offer table field to a default, empty value.
     */
    private resetOfferTableField(): void {
        this.properties.offerTableField.value = '';
    }

    /**
     * Resets the offer ID field to a default, empty value.
     */
    private resetOfferIdField(): void {
        this.properties.offerIdField.value = '0';
    }

    /**
     * Gets all currently selected values from the form fields.
     *
     * @returns {FormValues}
     */
    private getPropertyValues(): FormValues {
        const selectedStatusRadio = this.getSelectedStatusRadio();
        let status = 0;
        if (selectedStatusRadio) {
            status = parseInt(selectedStatusRadio.value);
        }

        const selectedSideOfferRadio = this.getSelectedSideOfferRadio();
        let sideOffer = 0;
        if (selectedSideOfferRadio) {
            sideOffer = parseInt(selectedSideOfferRadio.value);
        }

        return {
            'status': status,
            'price': parseFloat(FormHelpers.sanitizeFloat(this.properties.priceField.value)),
            'priceVariant': parseInt(this.properties.priceVariantSelect.value),
            'priceType': parseInt(this.properties.priceTypeSelect.value),
            'sideOffer': sideOffer,
            'vendor': this.properties.vendorField.value,
            'offerTitle': this.properties.offerTitleField.value,
            'offerTable': this.properties.offerTableField.value,
            'offerId': parseInt(this.properties.offerIdField.value)
        };
    }

    /**
     * Initializes the form with previously saved values and such.
     */
    private initializeForm(): void {
        this.initializeStatus();
        this.initializePriceVariant();
        this.initializeSideOffers();

        // Calculate new per person price, when the price changes; price can be calculated here for all use cases, as only a single price field and price
        // variant select exists.
        if (this.isPackagePriceCalculationActivated()) {
            this.properties.priceField.addEventListener('keyup', () => {
                this.updatePerPersonPrice();
            });
        }

        this.form.addEventListener('submit', () => {
            this.prepareSubmit();
        });
    }

    /**
     * Initializes the status form elements.
     */
    private initializeStatus(): void {
        let selectStatusRadio = this.getSelectedStatusRadio();

        // If no status is selected yet, use the first one as a default
        if (!selectStatusRadio) {
            selectStatusRadio = this.properties.statusRadios[0];
            selectStatusRadio.checked = true;
        }

        this.properties.statusRadios.forEach(item => {
            item.addEventListener('change', () => {
                this.resetPriceFields();
                this.updateFields();
            });
        });
    }

    /**
     * Initializes the price variant form elements.
     */
    private initializePriceVariant(): void {
        // If no price variant is selected yet, use 0 as a default
        if (!this.properties.priceVariantSelect.value) {
            this.properties.priceVariantSelect.value = '0';
        }

        // Update for initial price variant
        this.updatePerPersonPrice();

        // Update on price variant change
        this.properties.priceVariantSelect.addEventListener('change', () => {
            this.updatePerPersonPrice();
            this.updateFields();
        });
    }

    /**
     * Initializes the side offer form elements.
     */
    private initializeSideOffers(): void {
        // No side offers available
        if (!this.sideOffersContainer) {
            return;
        }

        // Show status radio button
        const sideOfferRadio = this.getSideOfferRadio();
        if (sideOfferRadio) {
            sideOfferRadio.closest('.form-check')?.classList.remove('d-none');
        }

        // No default value is applied; side offers need to be chosen individually
        this.properties.sideOfferRadios.forEach(item => {
            item.addEventListener('change', () => {
                this.resetPriceFields();
                this.updateFields();
            });
        });
    }

    /**
     * Updates all fields fitting the current status, side offer and/or price variant setting. This includes moving needed form fields to this container. If
     * fields are not needed for the current configuration, they are reset to default values.
     */
    private updateFields(): void {
        const propertyValues = this.getPropertyValues();

        // Hide everything by default
        this.disablePropertiesContainers();
        this.disableSideOffersContainer();
        this.disableOfferContainer();

        switch (propertyValues.status) {
            // Side offer status is selected
            case OfferFormAction.status.sideOffer:
                // Reset properties to prevent saving data not fitting this use case
                this.resetVendorField();
                this.resetOfferTitleField();
                this.resetOfferTableField();
                this.resetOfferIdField();
                this.enableSideOffersContainer();
                this.enablePropertyContainer();
                break;

            // External vendor/offer status is selected
            case OfferFormAction.status.offer:
                this.resetSideOfferRadios();
                this.enableOfferContainer();
                this.enablePropertyContainer();
                break;

            // Not decided or not needed is selected
            default:
                this.resetProperties();
                break;
        }
    }

    /**
     * Enables the side offers container. This includes moving needed form fields to this container.
     */
    private enableSideOffersContainer(): void {
        if (this.sideOffersContainer) {
            this.sideOffersContainer.classList.remove('d-none');
        }
    }

    /**
     * Disables the side offers container.
     */
    private disableSideOffersContainer(): void {
        if (this.sideOffersContainer) {
            this.sideOffersContainer.classList.add('d-none');
        }
    }

    /**
     * Enables the offers container. This includes moving needed form fields to this container.
     */
    private enableOfferContainer(): void {
        if (this.offerContainer) {
            this.offerContainer.classList.remove('d-none');
        }
    }

    /**
     * Disables the offers container.
     */
    private disableOfferContainer(): void {
        if (this.offerContainer) {
            this.offerContainer.classList.add('d-none');
        }
    }

    /**
     * Gets the property container fitting the current status, side offer and/or price variant setting.
     *
     * @returns {HTMLElement|null} Price container
     */
    private getPropertyContainer(): HTMLElement | null {
        const propertyValues = this.getPropertyValues();
        const status = propertyValues.status;
        const sideOffer = propertyValues.sideOffer;
        return this.container.querySelector<HTMLElement>(`[data-status="${status}"][data-side-offer="${sideOffer}"]`);
    }

    /**
     * Enables the property container fitting the current status, side offer and/or price variant setting. This includes moving needed form fields to this
     * container.
     */
    private enablePropertyContainer(): void {
        const propertyValues = this.getPropertyValues();
        const container = this.getPropertyContainer();

        // If no price container is found, the status probably has no additional properties
        if (!container) {
            return;
        }

        container.classList.remove('d-none');

        // Find available properties; some elements are only present for certain status, side offer and price variant configurations
        const vendorContainer = container.querySelector<HTMLElement>('.vendor');
        const priceContainer = container.querySelector<HTMLElement>('.price');
        const priceVariantContainer = container.querySelector<HTMLElement>('.price-variant');
        const priceTypeContainer = container.querySelector<HTMLElement>('.price-type');
        const priceInfoContainer = container.querySelector<HTMLElement>('.price-info');

        // If the current property container needs the vendor field, then move the common vendor field into that container
        if (vendorContainer) {
            vendorContainer.append(this.properties.vendorField.closest('.property') ?? '');
        } // Otherwise the vendor field is not needed in this case; move the vendor field back to the container of all available properties. This is needed,
        // to prevent showing the vendor field if it is not even needed, when changing the status radio buttons.
        else {
            this.availablePropertiesContainer.append(this.properties.vendorField.closest('.property') ?? '');
        }

        if (priceContainer) {
            priceContainer.append(this.properties.priceField.closest('.property') ?? '');

            // Adjust form label for the price depending on the selected price variant
            const priceLabel = priceContainer.querySelector<HTMLElement>('label[for="price"]');
            if (priceLabel) {
                if (propertyValues.priceVariant === OfferFormAction.priceVariant.package) {
                    priceLabel.textContent = LanguageUtility.getLabel('packagePrice');
                } else {
                    priceLabel.textContent = LanguageUtility.getLabel('pricePerPerson');
                }
            }
        } else {
            this.availablePropertiesContainer.append(this.properties.priceField.closest('.property') ?? '');
        }

        if (priceVariantContainer) {
            priceVariantContainer.append(this.properties.priceVariantSelect.closest('.property') ?? '');
        } else {
            this.availablePropertiesContainer.append(this.properties.priceVariantSelect.closest('.property') ?? '');
        }

        if (priceTypeContainer) {
            priceTypeContainer.append(this.properties.priceTypeSelect.closest('.property') ?? '');
        } else {
            this.availablePropertiesContainer.append(this.properties.priceTypeSelect.closest('.property') ?? '');
        }

        if (this.properties.packagePriceContainer) {
            if (
                priceInfoContainer &&
                this.isPackagePriceCalculationActivated() &&
                propertyValues.priceVariant === OfferFormAction.priceVariant.package
            ) {
                priceInfoContainer.append(this.properties.packagePriceContainer.closest('.property') ?? '');
            } else {
                this.availablePropertiesContainer.append(this.properties.packagePriceContainer.closest('.property') ?? '');
            }
        }
    }

    /**
     * Disables all properties containers.
     */
    private disablePropertiesContainers(): void {
        this.propertiesContainers.forEach(item => {
            item.classList.add('d-none');
        });
    }

    /**
     * Updates the per person price, if the plugin settings allow for dynamic calculation.
     */
    private updatePerPersonPrice(): void {
        if (!this.isPackagePriceCalculationActivated() || !this.properties.calculatedPriceElement || !this.properties.pricePerPersonTooltip) {
            return;
        }

        const price = parseFloat(FormHelpers.sanitizeFloat(this.properties.priceField.value));
        const perPersonPrice = OfferUtility.calculatePricePerPerson(price, this.numberOfAttendants + this.numberOfPupils);

        let tooltipText = LanguageUtility.getLabel('pricePerPersonInfo');
        tooltipText = tooltipText.replace('%2s', this.numberOfPupils.toString());
        tooltipText = tooltipText.replace('%3s', this.numberOfAttendants.toString());

        if (!isNaN(perPersonPrice) && perPersonPrice > 0) {
            const humanReadablePrice = '€ ' + price.toFixed(2);
            const humanReadablePerPersonPrice = '€ ' + perPersonPrice.toFixed(2);
            this.properties.calculatedPriceElement.innerText = humanReadablePerPersonPrice;
            tooltipText = tooltipText.replace('%1s', humanReadablePrice);
        } else {
            this.properties.calculatedPriceElement.innerText = '-';
            tooltipText = tooltipText.replace('%1s', LanguageUtility.getLabel('price'));
        }

        // Update tooltip
        const pricePerPersonTooltip = this.properties.pricePerPersonTooltip;
        if (pricePerPersonTooltip) {
            const tooltip = Tooltip.getOrCreateInstance(pricePerPersonTooltip);
            tooltip.dispose();
            pricePerPersonTooltip.dataset.bsTitle = tooltipText;
            Tooltip.getOrCreateInstance(pricePerPersonTooltip);
        }
    }

    /**
     * Prepares the form and form fields for submitting.
     */
    private prepareSubmit(): void {

    }

    /**
     * Initializes offer list on this checklist step.
     */
    private initializeOfferList(): void {
        this.offerListContainers.forEach(listContainer => {
            const offers = listContainer.querySelectorAll<HTMLElement>('.offer');
            if (offers.length === 0) {
                return;
            }

            // Get the currently saved values from the offer form
            const propertyValues = this.getPropertyValues();
            offers.forEach(offer => {
                const selectButtons = offer.querySelectorAll<HTMLButtonElement>('button.select-offer');
                const deselectButtons = offer.querySelectorAll<HTMLButtonElement>('button.deselect-offer');
                if (!offer.dataset.offerTable || !offer.dataset.offerId || !selectButtons || !deselectButtons) {
                    return;
                }
                const offerTable = offer.dataset.offerTable;
                const offerId = parseInt(offer.dataset.offerId);

                // If the offer's values are equal to the currently saved values in the offer form, the offer must be displayed as "selected"
                const isSelected = propertyValues.offerTable === offerTable && propertyValues.offerId === offerId;

                // Initialize the select-offer buttons
                selectButtons.forEach(button => {
                    button.addEventListener('click', () => {
                        // Reset properties
                        this.resetProperties();

                        // Set the "offer" radio button as selected
                        this.resetStatusRadios();
                        const offerRadio = this.getOfferRadio();
                        if (offerRadio) {
                            offerRadio.checked = true;
                        }

                        // Set additional fields according to the selected offer
                        this.properties.priceField.value = offer.dataset.offerPrice ?? '';
                        this.properties.vendorField.value = offer.dataset.offerVendor ?? '';
                        this.properties.offerTitleField.value = offer.dataset.offerTitle ?? '';
                        this.properties.offerTableField.value = offer.dataset.offerTable ?? '';
                        this.properties.offerIdField.value = offer.dataset.offerId ?? '';

                        this.form.submit();
                    });

                    // Hide the select-button, if the offer is already selected
                    if (isSelected) {
                        button.classList.add('d-none');
                    }
                });

                // Initialize the deselect-offer buttons
                deselectButtons.forEach(button => {
                    button.addEventListener('click', () => {
                        // Reset properties to prevent saving data not fitting this use case
                        this.resetProperties();

                        // Set the "not decided" radio button as selected
                        this.resetStatusRadios();
                        const notDecidedRadio = this.getNotDecidedRadio();
                        if (notDecidedRadio) {
                            notDecidedRadio.checked = true;
                        }

                        this.form.submit();
                    });

                    // Hide the deselect-button, if the offer is not selected currently
                    if (!isSelected) {
                        button.classList.add('d-none');
                    }
                });
            });
        });
    }

    /**
     * Initializes all modals for confirming certain actions like deleting offers.
     */
    private initializeModals(): void {
        // Initialize modal to remove an offer
        const removeModal = document.querySelector<HTMLElement>('#removeModal');
        if (removeModal) {
            const removeConfirmation = new ConfirmationModal(removeModal);
            removeConfirmation.emitter.on('confirm', (link: HTMLElement) => {
                const href = link.getAttribute('href');
                if (href) {
                    window.location.href = href;
                }
            });

            this.container.querySelectorAll<HTMLElement>('[data-action="remove"]').forEach(link => {
                link.addEventListener('click', e => {
                    e.preventDefault();
                    removeConfirmation.value = link;
                    removeConfirmation.show();
                });
            });
        }
    }
}
